import { ArrowBack, DeleteForever, Lock, LockOpen, PictureAsPdf } from '@mui/icons-material'
import { Button, Divider, Grid } from '@mui/material'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { FedExShipmentDto } from 'dtos/fedEx'
import { OhioOutgoingTitleDto } from 'dtos/ohioOutgoingTitles'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ManageVehiclesCard from './ManageVehiclesCard'
import OhioOutgoingTitlesDetailsCard from './OhioOutgoingTitlesDetailsCard'
import OutgoingTitlesGroupFedexLabelCard from './OutgoingTitlesGroupFedexLabelCard'

export default function OhioOutgoingTitlesDetails() {
  const { ohioOutgoingTitleGuid } = useParams()
  const layout = useLayout()
  const navigate = useNavigate()

  const [fedExShipment, setFedExShipment] = useState<FedExShipmentDto | undefined>()
  const [ohioOutgoingTitle, setOhioOutgoingTitle] = useState<OhioOutgoingTitleDto>(new OhioOutgoingTitleDto())

  useEffect(() => {
    if (!ohioOutgoingTitleGuid) return

    const isNew: boolean = ohioOutgoingTitleGuid.trim().toUpperCase() === 'NEW'

    layout.setIsLayoutLoading(true)
    if (isNew) {
      controllers.ohioOutgoingTitles
        .createOhioOutgoingTitleGroup(new OhioOutgoingTitleDto())
        .then(res => {
          enqueueSnackbar('Ohio Outgoing Title Created Successfully!', { variant: 'success' })
          navigate(`/ohio-outgoing-titles/${encodeURIComponent(res.value.ohioOutgoingTitleGuid!)}`, { replace: true })
        })
        .catch(() => navigate(-1))
    } else {
      controllers.ohioOutgoingTitles
        .getOhioOutgoingTitleGroup(ohioOutgoingTitleGuid)
        .then(res => {
          setOhioOutgoingTitle(res.value)
        })
        .finally(() => {
          layout.setIsLayoutLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ohioOutgoingTitleGuid])

  useEffect(() => {
    layout.setTitle('Ohio Outgoing Title Details')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  return (
    <>
      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <OhioOutgoingTitlesDetailsCard fedExShipment={fedExShipment} ohioOutgoingTitle={ohioOutgoingTitle}>
            <Grid item>
              <Button
                onClick={() => {
                  navigate('/ohio-outgoing-titles')
                }}
                color='primary'
                startIcon={<ArrowBack />}
                variant='text'
              >
                BACK
              </Button>
            </Grid>

            <Grid item sx={{ alignSelf: 'stretch' }}>
              <Divider orientation='vertical' />
            </Grid>

            <Grid item>
              <Button
                disabled={!ohioOutgoingTitle.ohioOutgoingTitleGuid || !ohioOutgoingTitle.isLocked || !fedExShipment?.fedExShipmentGuid}
                endIcon={<PictureAsPdf />}
                onClick={() => {
                  layout.setIsLayoutLoading(true)
                  controllers.fedEx.generateFedExLabels([fedExShipment!.fedExShipmentGuid!]).finally(() => {
                    layout.setIsLayoutLoading(false)
                  })
                }}
                variant='text'
              >
                GENERATE OUTGOING TITLES GROUP FEDEX LABEL
              </Button>
            </Grid>

            <Grid item>
              <Button
                endIcon={ohioOutgoingTitle.isLocked ? <LockOpen /> : <Lock />}
                onClick={() => {
                  layout.setIsLayoutLoading(true)
                  if (ohioOutgoingTitle.isLocked === true) {
                    controllers.ohioOutgoingTitles
                      .unlockOhioOutgoingTitleGroup(ohioOutgoingTitle.ohioOutgoingTitleGuid!)
                      .then(response => {
                        setOhioOutgoingTitle(response.value)
                        enqueueSnackbar('Ohio Outgoing Title Unlocked Successfully!', { variant: 'success' })
                        setFedExShipment(undefined)
                      })
                      .finally(() => {
                        layout.setIsLayoutLoading(false)
                      })
                  } else {
                    controllers.ohioOutgoingTitles
                      .lockOhioOutgoingTitleGroup(ohioOutgoingTitle.ohioOutgoingTitleGuid!)
                      .then(response => {
                        setOhioOutgoingTitle(response.value)
                        enqueueSnackbar('Ohio Outgoing Title Locked Successfully!', { variant: 'success' })
                      })
                      .finally(() => {
                        layout.setIsLayoutLoading(false)
                      })
                  }
                }}
                variant='text'
              >
                {ohioOutgoingTitle.isLocked ? 'UNLOCK' : 'LOCK'}
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='error'
                disabled={!ohioOutgoingTitleGuid || ohioOutgoingTitle?.isLocked}
                endIcon={<DeleteForever />}
                variant='text'
                onClick={() => {
                  if (window.confirm('Are you sure you would like to delete this Ohio Outgoing Title? This cannot be undone.')) {
                    layout.setIsLayoutLoading(true)
                    controllers.ohioOutgoingTitles.deleteOhioOutgoingTitleGroup(ohioOutgoingTitleGuid!).then(() => {
                      enqueueSnackbar('Ohio Outgoing Title Deleted Successfully!', { variant: 'success' })
                      navigate('/ohio-outgoing-titles', { replace: true })
                    })
                  }
                }}
              >
                DELETE
              </Button>
            </Grid>
          </OhioOutgoingTitlesDetailsCard>
        </Grid>

        <Grid item container xs={12} alignItems='flex-start'>
          <Grid item xs={8}>
            <ManageVehiclesCard ohioOutgoingTitle={ohioOutgoingTitle} setOhioOutgoingTitle={setOhioOutgoingTitle} />
          </Grid>

          <Grid item xs={4}>
            <OutgoingTitlesGroupFedexLabelCard
              fedExShipment={fedExShipment}
              setFedExShipment={setFedExShipment}
              isLocked={ohioOutgoingTitle.isLocked ?? true}
              ohioOutgoingTitleGuid={ohioOutgoingTitle.ohioOutgoingTitleGuid}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
